if (!customElements.get('rb-ingredient-showcase')) {
  class RbIngredientShowcase extends HTMLElement {
    connectedCallback() {
      if (this.initialized || this.dataset.rbShowcaseInitialized === 'true') return;

      this.track = this.querySelector('[data-rb-showcase-track]');
      this.cards = Array.from(this.querySelectorAll('.rb-ingredient-showcase__card'));
      this.previousButton = this.querySelector('[data-rb-showcase-prev]');
      this.nextButton = this.querySelector('[data-rb-showcase-next]');

      if (!this.track || !this.cards.length) return;

      this.initialized = true;
      this.dataset.rbShowcaseInitialized = 'true';
      this.index = 0;
      this.timer = null;
      this.resumeTimer = null;
      this.scrollFrame = null;
      this.isVisible = !('IntersectionObserver' in window);
      this.isInteracting = false;
      this.autoplay = this.dataset.autoplay === 'true';
      this.speed = Math.max(1000, Number(this.dataset.speed) || 2000);
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.onPrevious = () => {
        this.goTo(this.index - 1);
        this.restart();
      };
      this.onNext = () => {
        this.goTo(this.index + 1);
        this.restart();
      };
      this.onMouseEnter = () => {
        this.isInteracting = true;
        this.stop();
      };
      this.onMouseLeave = () => {
        this.isInteracting = false;
        this.start();
      };
      this.onFocusIn = () => {
        this.isInteracting = true;
        this.stop();
      };
      this.onFocusOut = (event) => {
        if (this.contains(event.relatedTarget)) return;
        this.isInteracting = false;
        this.start();
      };
      this.onTouchStart = () => {
        this.isInteracting = true;
        this.stop();
      };
      this.onTouchEnd = () => {
        window.clearTimeout(this.resumeTimer);
        this.resumeTimer = window.setTimeout(() => {
          this.isInteracting = false;
          this.start();
        }, 1200);
      };
      this.onScroll = () => {
        window.cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = window.requestAnimationFrame(() => {
          const step = this.step;
          if (!step) return;
          this.index = Math.min(this.maxIndex, Math.max(0, Math.round(this.track.scrollLeft / step)));
          this.updateControls();
        });
      };
      this.onVisibilityChange = () => {
        if (document.hidden) this.stop();
        else this.start();
      };
      this.onMotionChange = () => {
        this.stop();
        this.start();
      };
      this.onResize = () => this.goTo(this.index, 'auto');

      this.previousButton?.addEventListener('click', this.onPrevious);
      this.nextButton?.addEventListener('click', this.onNext);
      this.addEventListener('mouseenter', this.onMouseEnter);
      this.addEventListener('mouseleave', this.onMouseLeave);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
      this.track.addEventListener('touchstart', this.onTouchStart, { passive: true });
      this.track.addEventListener('touchend', this.onTouchEnd, { passive: true });
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      this.motionQuery.addEventListener?.('change', this.onMotionChange);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(this.track);
      } else {
        window.addEventListener('resize', this.onResize, { passive: true });
      }

      if ('IntersectionObserver' in window) {
        this.intersectionObserver = new IntersectionObserver(
          ([entry]) => {
            this.isVisible = entry.isIntersecting;
            if (this.isVisible) this.start();
            else this.stop();
          },
          { rootMargin: '120px 0px' }
        );
        this.intersectionObserver.observe(this);
      }

      this.goTo(0, 'auto');
      this.start();
    }

    disconnectedCallback() {
      if (!this.initialized) return;

      this.stop();
      window.clearTimeout(this.resumeTimer);
      window.cancelAnimationFrame(this.scrollFrame);
      this.previousButton?.removeEventListener('click', this.onPrevious);
      this.nextButton?.removeEventListener('click', this.onNext);
      this.removeEventListener('mouseenter', this.onMouseEnter);
      this.removeEventListener('mouseleave', this.onMouseLeave);
      this.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('focusout', this.onFocusOut);
      this.track.removeEventListener('touchstart', this.onTouchStart);
      this.track.removeEventListener('touchend', this.onTouchEnd);
      this.track.removeEventListener('scroll', this.onScroll);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.motionQuery.removeEventListener?.('change', this.onMotionChange);
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();

      if (!this.resizeObserver) {
        window.removeEventListener('resize', this.onResize);
      }

      this.initialized = false;
      delete this.dataset.rbShowcaseInitialized;
    }

    get step() {
      const styles = window.getComputedStyle(this.track);
      const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
      return (this.cards[0]?.offsetWidth || 0) + gap;
    }

    get visibleCount() {
      const step = this.step;
      return step ? Math.max(1, Math.floor((this.track.clientWidth + 1) / step)) : 1;
    }

    get maxIndex() {
      return Math.max(0, this.cards.length - this.visibleCount);
    }

    goTo(nextIndex, behavior = 'smooth') {
      this.index = Math.min(this.maxIndex, Math.max(0, nextIndex));
      this.track.scrollTo({
        left: this.step * this.index,
        behavior: this.motionQuery.matches ? 'auto' : behavior,
      });
      this.updateControls();
    }

    updateControls() {
      if (this.previousButton) this.previousButton.disabled = this.index <= 0;
      if (this.nextButton) this.nextButton.disabled = this.index >= this.maxIndex;
    }

    advance() {
      this.goTo(this.index >= this.maxIndex ? 0 : this.index + 1);
    }

    start() {
      if (
        !this.autoplay ||
        this.motionQuery.matches ||
        this.isInteracting ||
        !this.isVisible ||
        document.hidden ||
        this.maxIndex === 0
      ) {
        return;
      }

      this.stop();
      this.timer = window.setInterval(() => this.advance(), this.speed);
    }

    stop() {
      if (this.timer) window.clearInterval(this.timer);
      this.timer = null;
    }

    restart() {
      this.stop();
      this.start();
    }
  }

  customElements.define('rb-ingredient-showcase', RbIngredientShowcase);
}
